import { ChangeEvent, useEffect, useState } from 'react';
import { IconPencil, IconCheckbox } from '@tabler/icons-react';
import { Label, TextInput } from '@uhg-harmony/uitk-react';
import styled from 'styled-components';

interface EditableCustomThemeLabelProps {
  themeKey: number;
  themeName?: string;
  site: Site;
  updateSite: (site: Site) => void;
}

const ActiveEditingWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 0.5rem;
`;

const InactiveEditingWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 0.5rem;
  cursor: pointer;
`;

const StyledIconPencil = styled(IconPencil)`
  :hover {
    background-color: #d9f6fa;
    border-radius: 5px;
    outline: solid 1px #c7c7c7;
  }
`;

const StyledIconCheckbox = styled(IconCheckbox)`
  :hover {
    background-color: #d9f6fa;
    border-radius: 5px;
    outline: solid 1px #c7c7c7;
  }
`;

const FieldLabel = styled(Label)`
  padding: 0.75rem 0 0 0.5rem;
  font-family: OptumSans, sans-serif;
  font-size: 0.875rem;
  justify-items: flex-start;
`;

const EditableCustomThemeLabel = (props: EditableCustomThemeLabelProps) => {
  const {
    themeKey,
    themeName = 'Default Theme Name',
    site,
    updateSite,
  } = props;
  const [currentThemeName, setCurrentThemeName] = useState(themeName);
  const [isEditingCustomThemeLabel, setIsEditingCustomThemeLabel] = useState(
    false
  );
  const beginThemeNameChange = (e: React.MouseEvent<HTMLOrSVGElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsEditingCustomThemeLabel(true);
  };

  const handleThemeNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    e.preventDefault();
    setCurrentThemeName(e.target.value);
  };

  const confirmThemeNameChange = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsEditingCustomThemeLabel(false);
    updateSite({
      ...site,
      theme: {
        ...site.theme,
        [e.currentTarget.id]: {
          ...site.theme[e.currentTarget.id],
          name: currentThemeName,
        },
      },
    });
  };

  useEffect(() => {
    setCurrentThemeName(themeName);
  }, [themeName]);

  return (
    <>
      {isEditingCustomThemeLabel ? (
        <ActiveEditingWrapper>
          <TextInput
            id={`customTheme${themeKey}`}
            value={currentThemeName}
            type="text"
            placeholder="Enter theme name"
            onBlur={confirmThemeNameChange}
            onChange={handleThemeNameChange}
            autoFocus
          />
          <StyledIconCheckbox
            onClick={confirmThemeNameChange}
            style={{ cursor: 'pointer', marginLeft: '0.5rem' }}
          />
        </ActiveEditingWrapper>
      ) : (
        <InactiveEditingWrapper>
          <FieldLabel>{currentThemeName}</FieldLabel>
          <StyledIconPencil
            onClick={beginThemeNameChange}
            style={{ cursor: 'pointer', marginLeft: '0.5rem' }}
          />
        </InactiveEditingWrapper>
      )}
    </>
  );
};

export default EditableCustomThemeLabel;
